import store from '../store';
import moment from "moment";
import constants from "@/constants";
import swal from "sweetalert2";
import * as authTypes from "@/store/auth/auth.types";
import { type } from 'jquery';

export const hasParentSession = () => {
  return store.state.auth.parentSession;
};

export const consultantLoginData = () => {
  return store.state.auth.parentSession;
};

export const accessedEmployeeTypes = () => {
  if(hasParentSession()) {
    return hasParentSession().employee_types;
  } else if(isCorporateAdmin()) {
    return additionalAccess().employee_types
  } else {
    return []
  }
};

export const isUserLoggedin = () =>
  Boolean(store.state.auth.userData && store.state.auth.userData.auth_data);

export const isCorporateUser = () => {
  return (
    (store.state.auth.userData &&
      store.state.auth.userData.user &&
      store.state.auth.userData.user.entity_type_object &&
      store.state.auth.userData.user.entity_type_object.id) === 2
  );
};

export const additionalAccess = () => {
  return (
    store.state.auth.userData &&
    store.state.auth.userData.auth_data &&
    store.state.auth.userData.auth_data.extra_details
  );
}

export const isCorporateAdmin = () => {
  return Boolean(additionalAccess() && additionalAccess().is_corporation_admin);
};

export const isCorporateAdminsEnabled = () => {
  return (isCorporateUser() &&
    (store.state.auth.userData &&
      store.state.auth.userData.user &&
      store.state.auth.userData.user.entity &&
      store.state.auth.userData.user.entity.allow_admins) === 1
  );
};

export const filteredEmployeeTypes = (employeeTypes) => {
  const predefinedEmpTypes = accessedEmployeeTypes();
  if(predefinedEmpTypes.length) {
    return employeeTypes.filter(eT => predefinedEmpTypes.includes(eT.id))
  } else if(hasParentSession()) {
    return [employeeTypes[0]];
  } else {
    return employeeTypes;
  }
};

export const isSubAdmin = () => {
  return (
    (store.state.auth.userData &&
      store.state.auth.userData.auth_data &&
      store.state.auth.userData.auth_data.type) === 'admins' && !isSuperAdmin()
  );
};

export const isAdmin = () => {
  return (
    (store.state.auth.userData &&
      store.state.auth.userData.auth_data &&
      store.state.auth.userData.auth_data.type) === 'admins'
  );
};

export const isTamaAdmin = () => {
  return Boolean(store.state.auth.userData &&
      store.state.auth.userData.user &&
      store.state.auth.userData.user.consultant_id);
};

export const isSuperAdmin = () => {
  return (
    (store.state.auth.userData &&
      store.state.auth.userData.auth_data &&
      store.state.auth.userData.auth_data.email) === "super@admin.org"
  );
};

export const isHNWUser = () => {
  const hnwRoles = ['HNWI', 'HNWI (HEAD)', 'HNWI (UNIT STAFF)'];
  return hnwRoles.includes(store.state.auth.userData &&
    store.state.auth.userData.user &&
    store.state.auth.userData.user.role &&
    store.state.auth.userData.user.role.name.toUpperCase());
};

export const isHNWOffice = () => {
  const { user } = store.state.auth.userData;
  return user && user.office && user.office.system_code === 'hnw';
};

export const isPayer = () => {
  return (
    (store.state.auth.userData &&
      store.state.auth.userData.auth_data &&
      store.state.auth.userData.auth_data.type) === 'payers'
  );
};

/**
 * Generic way to search if tag exists for a payer
 * @param {String} tag
 * @returns {Object} If a tag exists or not
 */
export const searchTag = (profile, tag) => {
  const tags = (profile.tax_payer && profile.tax_payer.tags) || [];
  return tags.find(tg => tg.type && tg.type.code === tag);
};

/**
 * Generic way to handle raise assessment access
 * @param {Object} profile API Response data (response.data)
 * @returns {Boolean} If an hnw tagged or not
 */
export const canRaiseAssessment = (profile) => {
  const hnwTag = searchTag(profile, 'high_net_worth');
  return isHNWUser() ? hnwTag : true;
};

export const getUserData = () =>
  store.state.auth.userData && store.state.auth.userData.user;

export const getTaxStation = () =>
  (store.state.auth.userData && store.state.auth.userData.user && store.state.auth.userData.user.office) || {};

export const getRegion = () =>
  (getTaxStation() && getTaxStation().region) || {};

/**
 * Check the current environment and if all featuress should be rendered
 * @returns {Boolean}
 */
export const showAllFeatures = () =>
  !!parseInt(process.env.VUE_APP_SHOW_ALL_FEATURES);

/**
 * Generic way to handle API errors
 * @param {object} data API Response data (response.data)
 * @param {function} notifier this.notification function
 * @param {string} defaultMsg Default error message if non provided by API
 * @returns {Boolean} If an error occurred or not
 */
export const genericErrorHandler = (data, notifier, defaultMsg) => {
  if (!data) {
    notifier(defaultMsg, 'pan_tool', 'danger');
    return true;
  }

  if (data.error) {
    if (data.data) {
      for (let message in data.data) {
        notifier(data.data[message][0], 'pan_tool', 'danger');
      }
    } else {
      notifier(data.message || defaultMsg, 'pan_tool', 'danger');
    }
    return true;
  }

  return false;
};

/**
 * Helper to get values for a named reference
 */
export const getReferenceValues = (ref, reqClient) => {
  return new Promise((resolve, reject) => {
    reqClient
      .post(`references/${ref}/values`, {
        method: 'GET'
      })
      .then(response => {
        const { data } = response;

        if (data && !data.error) {
          resolve(data);
        } else {
          reject(data);
        }
      })
      .catch(err => {
        reject(err);
        this.notification(
          'Error loading page data, please refresh to retry',
          'pan_tool',
          'danger'
        );
      });
  });
};

/**
 * Utility to format numbers to look like numbers
 * @param {string|number} amount Amount
 * @returns
 */
export const formatMoney = amount => {
  if ((!amount && amount !== 0) || isNaN(amount)) {
    return '0.00';
  }

  return parseFloat(amount).toLocaleString(undefined, {
    minimumFractionDigits: 2
  });
};

/**
 * Sort an array of objects alphabetically
 * @param {array} arr Array
 * @param {string} sortKey Key to sort with
 */
export const sortAlphabetically = (arr, sortKey) =>
  arr.sort((a, b) => {
    const aName = (a[sortKey] && a[sortKey].toString().toLowerCase()) || '';
    const bName = (b[sortKey] && b[sortKey].toString().toLowerCase()) || '';

    return aName.localeCompare(bName, 'en', {
      sensitivity: 'base'
    });
  });

/**
 * Rearrange object entries
 * @param {array} arr Array
 */
export const reOrderObjItems = arr => {
  let response = {};

  arr.map(item => {
    response[item.id] = item.name;
  });

  return response;
};

/**
 * Rearrange object entries base on input
 * @param {array} arr Array
 * @param {array} data array
 */
export const inputBaseObjRender = (arr, data) => {
  let response = {};

  if (data.length > 1) {
    arr.map(item => {
      response[item[data[0]]] = `${item[data[1]]} - ${item[data[0]]}`;
    });
  } else {
    arr.map(item => {
      response[item[data[0]]] = item[data[0]];
    });
  }

  return response;
};

/**
 * Format number to readable currency digit
 * @param num Integer
 */
export const formatNumber = num => {
  return num.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
};

/**
 * Set possible error codes relating to accees denied
 * Check input againt the list
 * @param data string
 */
export const isAccessDenied = data => {
  const accessRelatedCodes = [
    'INVALID_CRED',
    'ACCESS_DENIED',
    'NO_AUTH',
    'INTERNAL_ERROR'
  ];
  return typeof data === 'string' && accessRelatedCodes.includes(data);
};

/**
 * Helper to clear session and localstorage upon logout
 */
export const logout = (thisScope, path=null) => {
  const navUrl = path || loginUrl();
  return thisScope.$http
    .post(`auth/logout`, {
      method: 'POST'
    })
    .then(response => {
      if (response.status === 200) {
        const isUserAdmin = isAdmin();
        store.dispatch(authTypes.LOGIN_USER, {});
        store.dispatch(authTypes.PARENT_LOGIN_USER, false);
        localStorage.removeItem('etax');
        sessionStorage.removeItem('user');
        thisScope.$router.push(navUrl + (isUserAdmin ? `?redirect=${window.location.pathname}` : ''));
      } else {
        notification(thisScope, 'Something went wrong while logging out of the system', 'clear', 'danger');
      }
    })
    .catch(err => {
      notification(thisScope, err.message, 'clear', 'danger');
    });
};

/**
 * Return similar name in property by comparing ID
 * @param {array} arr Array
 * @param {string} id String
 */

export const pickValue = (arr, id) => {
  let data = '';
  for (let loop in arr) {
    if (arr[loop] && arr[loop].id === id) {
      data = arr[loop].name;
      break;
    }
  }
  return data;
};

/**
 * Return if user is an individual or corporate tax payer
 * @param {string} id String
 */
export const payerType = id => {
  let data = 'Individual';
  if (id.includes('C') || id.includes('c')) {
    data = 'Corporate';
  }
  return data;
};

/**
 * Return valid format response
 * @param {string} data String
 */
export const validateCacNumber = data => {
  if(!data) return

  const value = data.toUpperCase();
  const pattern = new RegExp(/^([A-Z]{2})([\d]+)$/);
  return pattern.test(value);
};

/**
 * Utility to check validate BVN number to have 11 digits
 * @param {string|number} bvn String/Number
 * @returns {Boolean} If 11 digits or not
 */
export const validateBvn = bvn => {
  const value = Number(bvn);
  const pattern = new RegExp(/^(\d{11})$/);
  return pattern.test(value);
};

/**
 * Return valid format response
 * @param {string} email String
 */
 export const validateEmail = email => {
  const mailformat = new RegExp(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
  return mailformat.test(email);
};

/**
 * Return valid format response
 * @param {string} password String
 * @param {function} notifier this.notification function
 * @returns {Boolean} If an error occurred or not
 */
export const passwordValidation = (password, notifier) => {
  let isValid = true;
  const pwdLength = password.trim().length;
  if (pwdLength === 0) {
    isValid = false;
    notifier("Password can't be blank", "pan_tool", "danger");
    return isValid;
  }
  if (pwdLength < 8) {
    isValid = false;
    notifier("Password must be at least 8 characters", "pan_tool", "danger");
  }
  if (!/[a-z]+/.test(password)) {
    isValid = false;
    notifier("One lowercase letter required", "pan_tool", "danger");
  }
  if (!/[A-Z]+/.test(password)) {
    isValid = false;
    notifier("One uppercase letter required.", "pan_tool", "danger");
  }
  if (!/[!@#$%^&*]+/.test(password)) {
    isValid = false;
    notifier("One special character required", "pan_tool", "danger");
  }
  return isValid;
};

/**
 * Utility to convert 0 to null
 * @param {object} event event data of number field
 */
export const convertZeroToNull = event => {
  if(event.target.value === "0") {
    event.target.value = null;
  }
};

/**
 * Utility to convert 0 to null
 * @param {object} event event data of number field
 */
export const convertNullToZero = event => {
  if(event.target.value === "" || event.target.value === null) {
    event.target.value = 0;
  }
};

/**
 * Utility to convert 0 to null
 * @param {array} arr array of elements
 * @returns {Number}
 */
export const elementCount = arr => {
  if(!arr.length) {
    return 0;
  }
  const count = arr.reduce(function(prev, curr){
    const preVal = String(prev).replace(/[,]+/g, '');
    const crntVal = String(curr).replace(/[,]+/g, '');
    return (Number(preVal) || 0) + (Number(crntVal) || 0);
  });
  return count;
};

/**
 * Utility to fetch tax rates by slabs
 * @returns {Array}
 */
export const taxRates = () => {
  const taxRates = [
    {
      id: 'first',
      amount: 300000,
      tax: 7
    },
    {
      id: 'second',
      amount: 300000,
      tax: 11
    },
    {
      id: 'third',
      amount: 500000,
      tax: 15
    },
    {
      id: 'forth',
      amount: 500000,
      tax: 19
    },
    {
      id: 'fifth',
      amount: 1600000,
      tax: 21
    },
    {
      id: 'sixth',
      amount: 3200000,
      tax: 24
    }
  ];
  return taxRates;
};

/**
 * Return valid format response
 * @param {Object} lastDate String
 * @returns {Number}
 */
export const countDownByDate = lastDate => {
  const today = new Date();
  const startDate = moment([today.getFullYear(), today.getMonth(), today.getDate()]);
  const endDate = moment([lastDate.year(), lastDate.month(), lastDate.date()]);
  const dayDiff = endDate.diff(startDate, 'days');
  return dayDiff;
};

/**
 * Return valid format response
 * @param {string} fileType String
 * @param {string} fileName String
 * @param {function} notifier this.notification function
 * @returns {Boolean} If an error occurred or not
 */
export const isFile = (fileName, fileType, notifier) => {
  let isValid = true;
  const extension = fileName.split('.').pop().toUpperCase();
  const requiredExt = fileType.split('.').pop().toUpperCase();
  if(extension !== requiredExt) {
    notifier(`File must be a ${requiredExt} file`, "pan_tool", "danger");
    isValid = false;
  }
  return isValid;
};

export const isFileTypeIncluded = (fileName, fileType, notifier) => {
  let isValid = true;
  const extension = fileName.split('.').pop();
  if(fileType.indexOf("image") >= 0) {
    const imgMimeTypes = ['', 'jpg', 'jpeg', 'png', 'webp'];
    fileType = fileType.concat(imgMimeTypes.join(','));
  }
  if(fileType.indexOf(extension) < 0) {
    notifier(`File must be any of ${fileType} files`, "pan_tool", "danger");
    isValid = false;
  }
  return isValid;
};

/**
 * Utility to check length of a phone number to have 11 digits
 * @param {string|number} mobile String/Number
 * @returns {Boolean} If 11 digits or not
 */
export const isPhoneNumberOf11Digits = mobile => {
  const isNumber = !isNaN(mobile);
  return isNumber && mobile.length === 11;
};

/**
 * Returns an object grouped by a key of object from array
 * @param {array} data Array
 * @param {string} key String
 * @param {string} title String
 * @returns {Object} By key
 */
export const groupBy = (data, key, title = '') => {
  return data.reduce((result, currentValue) => {
    (result[`${currentValue[key]}${title ? '/'+currentValue[title] : ''}`] = result[`${currentValue[key]}${title ? '/'+currentValue[title] : ''}`] || []).push(
      currentValue
    );
    return result;
  }, {});
};

/**
 * Returns an array of keys of an object by its value array length
 * @param {Object} obj Object
 * @returns {Array} Array
 */
export const sortKeysByValue = (obj) => {
  // Sort keys by its values(collection) length
  return Object.keys(obj).sort(function(a,b){return obj[b].length - obj[a].length});
};

/**
 * Returns an object from url query string
 * @param {string} qString String
 * @returns {Object} By key
 */
export const convertParamsToObject = (qString) => {
  // return JSON.parse('{"' + decodeURI(qString).replace(/"/g, '\\"').replace(/&/g, '","').replace(/=/g,'":"') + '"}');
  const params = {};
  let hash;

  if(qString != undefined) {
    const queryParams = qString.split('&');

    for(let i = 0; i < queryParams.length; i++) {
      hash = queryParams[i].split('=');
      params[hash[0]] = hash[1];
      if(i === queryParams.length - 1) {
        return params;
      }
    }
  }
};

/**
 * Returns an object from url query string
 * @param {string} qString String
 * @returns {Object} By key
 */
export const getParamsByKey = (qString, key) => {
  const params = new URLSearchParams(qString);
  return JSON.parse(params.get(key));
};

/**
 * Returns chucks of array
 * @param {Array} arr Array
 * @param {Number} size Number
 * @returns {Array} returns chunks
 */
export const chunkArray = (arr, size) => {
  return Array.from({ length: Math.ceil(arr.length / size) }, (v, i) =>
    arr.slice(i * size, i * size + size)
  );
}

/**
* Utility to convert string number to number(ex: Amount: "12,156.23" => 12156.23)
* @param {string} str String
* @returns {Number} Converted number
*/
export const convertToNumber = str => {
  if(typeof str === 'number') return str;

  if(!str || (str && !str.trim().length)) return 0;

  const stringWithNoComma = str.replace(/,/g, '');
  const matchedNumber = stringWithNoComma.match(/[\d+,.]/g);
  if(matchedNumber) {
    return Number(matchedNumber.join(''));
  }
  return 0;
};

export const convertToLowerCase = (word) =>{
  return word.toLowerCase();
};

export const camelToSentence = str =>{
  const result = str.replace( /([A-Z])/g, " $1" );
  return result.charAt(0).toUpperCase() + result.slice(1);
};

export const readableText = str =>{
  return str.replace( /_/g, " " );
};

export const titleCase = str =>{
  return str
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, str => str.toUpperCase());
};

export const isUrl = str =>{
  const regexp = /(http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-/]))?/
  return regexp.test(str);
};

/**
 * Returns a boolean value from an object
 * @param {Object} obj Object
 * @returns {Boolean} If any value of the object is blank, null, undefined, NaN.
 */
export const objectValidation = (obj) => {
  const validationChecks = ["", null, undefined, NaN];
  const objValues = Object.values(obj);

  let isObjValid = true;
  objValues.forEach(function(av) {
    if(isObjValid && validationChecks.includes(av)){
      isObjValid = false;
    }
  })

  return isObjValid;
};

/**
 * Returns a full name of profile by concatinating first_name, middle_name and last_name
 * @param {Object} profile Object
 * @returns {String} If any value of the object is blank, null, undefined, NaN.
 */
export const profileFullName = profile => {
  if(!profile) return 'N/A';

  let fullName = profile && profile.first_name;
  if(!fullName) {
    // corporate profile will have 'name' attribute
    return profile ? profile.name ? profile.name : 'N/A' : 'N/A';
  }
  if(profile.middle_name) {
    fullName += ` ${profile.middle_name}`;
  }
  fullName += ` ${profile.last_name}`;

  return fullName;
};

/**
 * Returns a full name of profile by concatinating first_name, middle_name and last_name
 * @param {Object} profile Object
 * @returns {String} If any value of the object is blank, null, undefined, NaN.
 */
export const profilePhoneNum = profile => {
  if(!profile) {
    return 'N/A';
  }
  if(profile.telephone) {
    return profile.telephone;
  }
  if(profile.telephones) {
    return profile.telephones.join(', ');
  }
};

/**
 * Returns a full name of profile by concatinating first_name, middle_name and last_name
 * @param {Object} profile Object
 * @returns {String} If any value of the object is blank, null, undefined, NaN.
 */
 export const profileEmailId = profile => {
  if(!profile) {
    return 'N/A';
  }
  return profile.email;
};

/**
 * Return boolean based on date comparisions
 * @param {Object} startDate Object
 * @param {Object} endDate Object
 * @returns {Boolean}
 */
export const dateRangeValidation = (startDate, endDate, dateRange = { sameDayAllowed: true }) => {
  const stDate = moment(startDate).format();
  const enDate = moment(endDate).format();
  return dateRange.sameDayAllowed ? stDate <= enDate : stDate < enDate;
};

/**
 * Return boolean based on date comparisions
 * @param {Object} dob Object
 * @returns {Boolean}
 */
 export const dobValidation = (dob) => {
  return moment(dob).year() < moment().year();
};

/**
 * Return boolean if 2 given arrays are idential
 * @param {Array} arr1 Array
 * @param {Array} arr2 Array
 * @returns {Boolean}
 */
export const compareArrays = (arr1, arr2) => {
  let areIdentical = true;
  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) {
      areIdentical = false
      break;
    }
  }

  if(areIdentical) {
    return new Promise(resolve => {
      resolve(true)
    });
  } else {
    return swal({
      title: "Warning",
      text: `File contains invalid data: space, special character… Do you want to continue? Click Ok`,
      type: "warning",
      showCancelButton: true,
      confirmButtonText: "Ok",
      cancelButtonText: "Cancel",
      cancelButtonClass: "md-button md-default",
      confirmButtonClass: "md-button md-success",
      buttonsStyling: false
    }).then(result => {
      return result.value;
    });
  }
}

export const identialArrays = (arr1, arr2) => {
  let areIdentical = true;
  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) {
      areIdentical = false
      break;
    }
  }

  return areIdentical;
}

/**
 * Return field name with order by/ascending
 * @param {String} field Object
 * @param {Number} sort Object
 * @returns {String}
 */
export const orderBy = (field, sort = 1) => {
  return `&order_by=${field}&is_asc=${sort}`;
};

/**
 * Returns sum of elements of an array
 * @param {Array} arr Array
 * @returns {Number}
 */
export const sumOfElements = (arr) => {
  return arr.reduce((accumulator, element) => accumulator + element, 0);
}

/**
 * Returns if value is masked
 * @param {String} val String
 * @returns {Boolean}
 */
 export const isMaskedValue = (val) => {
  const isNotNum = isNaN(val);
  const masked = val.indexOf('*') >= 0;
  return isNotNum || masked;
}

/**
 * Return items by enpoint
 * @param {String} endPoint String
 * @param {String} order String
 * @returns {Object}
 */
export const getItemsByEndPoint = (endPoint, order = null, reqClient, filters = null) => {
  return new Promise((resolve, reject) => {
    let params = "&no_pages=1";
    if(order) {
      params += orderBy(order);
    }
    if(filters && filters.length) {
      params += `&filters=${JSON.stringify(filters)}`;
    }
    reqClient
      .post(endPoint, {
        method: 'GET',
        params: params
      },
      {
        params: {
          skipLoader: true
        }
      })
      .then(response => {
        const { data } = response;

        if (data && !data.error) {
          resolve(data);
        } else {
          reject(data);
        }
      })
      .catch(err => {
        reject(err);
        this.notification(
          'Error loading page data, please refresh to retry',
          'pan_tool',
          'danger'
        );
      });
  });
};

/**
 * Returns identification type systems
 * @param {Object} reqClient Object
 * @param {Array} params Array
 * @returns {Array}
 */
export const getIdentificationSystems = (reqClient, params=[]) => {
  let filters = [
    {
      "field": "tax_payer_entity_type_id",
      "value": constants.individualEntityTypeId
    }
  ];
  if(params.length) {
    filters = params;
  }
  const queryString = `filters=${JSON.stringify(filters)}`;
  let apiUrl = 'identification/systems';
  if(!getUserData()) {
    apiUrl = 'public/' + apiUrl;
  }

  return reqClient
    .post(apiUrl, {
      method: 'GET',
      params: queryString
    })
    .then(response => {
      const { data } = response;
      return data;
    })
    .catch(err => {
      return err;
    });
};

/**
 * Returns identification type systems
 * @param {Object} item Object
 * @returns {String}
 */
 export const getAccountOfficers = item => {
  let officers = [];

  if(item.tax_payer && item.tax_payer.admin_links && item.tax_payer.admin_links.length) {
    officers = item.tax_payer.admin_links.map(link => {
      const linkAdmin = link.admin;
      if(linkAdmin) {
        return `${linkAdmin.first_name} ${linkAdmin.last_name}(${linkAdmin.reg_no})`
      }
    });
    officers = officers.filter(cul => cul);
  }
  return officers.length ? officers.join(', ') : 'N/A';
}
export const getAssignedOfficers = item => {
  let officers = [];

  if(item.consultant_user_links && item.consultant_user_links.length) {
    officers = item.consultant_user_links.map(cul => cul.consultant_user && cul.consultant_user.tax_payer && cul.consultant_user.tax_payer.reg_no);

    officers = officers.filter(cul => cul);
  }
  return officers.length ? officers.join(', ') : '';
}

/**
 * Popout the alerts
 * @param {Object} response Object
 * @param {Object} vm Object
 * @param {String} method Object
 */
export const _handleErrors = (response, vm, method='GET') => {
  let dataMessage = false;
  const defaultMsg = 'Something not right!'

  if(typeof response === 'string') {
    vm.$notify({
      message: response || defaultMsg,
      icon: 'pan_tool'
    });
    return;
  }

  if (response.data) {
    for (let message in response.data) {
      if(response.data[message] && typeof response.data[message][0] !== 'undefined') {
        dataMessage = true;
        vm.$notify({
          message: response.data[message][0] || defaultMsg,
          icon: 'pan_tool',
          horizontalAlign: "right",
          verticalAlign: "top",
          type: 'danger'
        });
      }
    }
  }
  if(response.code === 'ACCESS_DENIED' && method !== 'GET') return;

  if(!dataMessage) {
    const errMsg = response.code === 'ACCESS_DENIED' ? 'You do not have access to perform actions on this page' : response.message;

    vm.$notify({
      message: errMsg || defaultMsg,
      icon: 'pan_tool',
      horizontalAlign: "right",
      verticalAlign: "top",
      type: 'danger'
    });
  }
}

/**
 * Returns if value exists
 * @param {String} val String
 * @returns {String}
 */
 export const getCookieByName = (val) => {
  const name = `${val}=`;
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookieItems = decodedCookie.split(';');
  for(let i = 0; i < cookieItems.length; i++) {
    let cookieItem = cookieItems[i];
    while (cookieItem.charAt(0) == ' ') {
      cookieItem = cookieItem.substring(1);
    }
    if (cookieItem.indexOf(name) == 0) {
      return cookieItem.substring(name.length, cookieItem.length);
    }
  }
  return "";
}

/**
 * Returns if value exists
 * @param {String} cName String
 * @param {String} cValue String
 * @returns {Boolean}
 */
 export const setCookieByName = (cName, cValue) => {
  const date = new Date();
  const exdays = 1;
  date.setTime(date.getTime() + (exdays*24*60*60*1000));
  const expires = "expires="+ date.toUTCString();
  document.cookie = `${cName}=${cValue}; expires=${expires}; path=/`;
  return true;
}

/**
 * Return filtered salutations
 * @param {Array} titles Array
 * @param {String} val String
 * @returns {Array}
 */
 export const filterSalutations = (titles, val) => {
  const ignorableTitles = val === 'female' ? constants.titlesByMale : constants.titlesByFeMale;
  return titles.filter(title => !ignorableTitles.includes(title.name));
}

/**
 * Return boolean based on date comparisions
 * @param {File} fileData Object
 * @returns {Boolean}
 */
export const isFileSizeValid = fileData => {
  if(!fileData) return false;

  const bitesForKB = 1024;
  const kbsForMb = 1024;
  const fileSizeInMB = fileData.size / bitesForKB / kbsForMb;
  return fileSizeInMB <= constants.fileUploadMaxSize;
};

/**
 * Return address object
 * @param {Object} address Object
 * @returns {String}
 */
export const addressByPayer = address => {
  return `<span>${address.address || '-'}</span><br>
  <span><strong>State:</strong> ${address.lga && address.lga.state.name ? address.lga.state.name : '-'} </span><br>
  <span><strong>LGA:</strong> ${address.lga && address.lga.name ? address.lga.name : '-'} </span><br>
  <span><strong>LCDA:</strong> ${address.lcda && address.lcda.name ? address.lcda.name : '-'} </span><br>`;
};

/**
 * Return sub array by array
 * @param {Number} limit Number
 * @param {Boolean} inclusiveCurrentYear Boolean
 * @param {Boolean} asObject Boolean
 * @returns {Array}
 */
export const fetchYears = (
  limit,
  inclusiveCurrentYear = false,
  asObject = false
) => {
  const years = constants.years;
  let result = years;

  if (limit && inclusiveCurrentYear) {
    result = years.slice(-limit);
  } else if (limit && !inclusiveCurrentYear) {
    result = years.slice(-limit - 1, -1);
  }

  // Sort in descending order
  result = result.sort((a, b) => b - a);

  if (asObject) {
    return result.map(year => ({ id: year, year }));
  }

  return result;
};

export const fetchYearsFromBaseYear = (
  baseYear = constants.baseYear,
  inclusiveCurrentYear = false,
  asObject = false
) => {
  const currentYear = moment().year();

  const maxYear = inclusiveCurrentYear
    ? currentYear
    : currentYear - 1;

  const years = [];

  for (let year = baseYear; year <= maxYear; year++) {
    years.push(year);
  }

  const resultYears = years.reverse();
  if (asObject) {
    return resultYears.map(year => ({ id: year, year }));
  }
  return resultYears;
};

/**
 * Return sub array by array
 * @param {Number} limit Number
 * @param {Number} endYear Number
 * @param {Boolean} inclusiveEndYear Boolean
 * @returns {Array}
 */
export const fetchYearsByLimit = (limit, endYear=moment().year(), inclusiveEndYear=false) => {
  const years = constants.years;
  const yearPos = years.indexOf(endYear);
  let resultedYears = years;
  if(limit && inclusiveEndYear) {
    resultedYears = years.slice(yearPos - limit, yearPos + 1);
  } else if(limit && !inclusiveEndYear) {
    resultedYears = years.slice(yearPos - limit, yearPos);
  } else {
    resultedYears = years;
  }
  const index_2020 = resultedYears.indexOf(2020);
  if(index_2020 >= 0) resultedYears = resultedYears.slice(index_2020);

  return resultedYears.reverse();
};


/**
 * Get uniq elements
 * @param { Array } arr Array
 * @returns {Array }
 */
export const uniq = (arr) => {
  return arr.filter((value, index, self) => self.indexOf(value) === index);
}

/**
 * Downloads a file
 * @param {Object} file Object
 * @returns {Void}
 */
export const downloadFile = (file, fileName=null) => {
	const element = document.createElement("a");
  const fileNameToDownload = fileName || file.name || file.url.split('/').pop();
  element.setAttribute("href", file.url);
  element.setAttribute("target", "_blank");
  element.setAttribute("download", fileNameToDownload);
  element.style.display = "none";
  element.click();
  element.remove();
}

export const truncate = (text, length = 45) => {
  if (!text) return "-";
  return text.length > length ? `${text.slice(0, length)}...` : text;
}

/**
 * Scroll the position
 * @param {Number} xCoOrd Number
 * @param {Number} yCoOrd Number
 * @returns {Void}
 */
export const scrollTo = (xCoOrd=0, yCoOrd=0) => {
	window.scrollTo({
    behaviour: "smooth",
    top: yCoOrd,
    left: xCoOrd
  });
}

/**
 * Address validation
 * @param {Object} address Object
 * @param {Object} notifier Object
 * @returns {Boolean}
 */
export const validateAddressInput = (address, notifier) => {
  let isValidAddress = true;
  if(!address.street_number) {
    isValidAddress = false;
    notifier("Street Number is required", "pan_tool", "danger");
  }
  if(!address.street_name) {
    isValidAddress = false;
    notifier("Street Name is required", "pan_tool", "danger");
  }
  if(!address.state_id) {
    isValidAddress = false;
    notifier("State is required", "pan_tool", "danger");
  }
  if(!address.lga_id) {
    isValidAddress = false;
    notifier("LGA is required", "pan_tool", "danger");
  }
  if(address.isLcdaMandatory && !address.lcda_id) {
    isValidAddress = false;
    notifier("LCDA is required", "pan_tool", "danger");
  }

  return isValidAddress;
};
/**
 * Void
 * @param {Array} dates Array
 */
export const clearDatePickers = (vm, dates) => {
  dates.forEach(date => {
    const currentDate = vm.$refs[date];
    currentDate.selectedDate = null;
    const currentDateClr = currentDate.$el.querySelector('button.md-clear');
    if(currentDateClr) currentDateClr.click();
  });
}

// Login Url based on User role
export const loginUrl = () => {
  let url = '';
  if(isCorporateAdmin()) {
    url = '/corporate-admin/login'
  } else if(isTamaAdmin()) {
    url = '/consultant/login'
  } else if(isAdmin()) {
    url = '/admin/login'
  } else {
    url = '/login';
  }
  return url;
};

// Default Profile Url based on User role
export const profileUrl = () => {
  let url = '';
  if(isTamaAdmin()) {
    url = '/consultant/dashboard'
  } else if(isAdmin()) {
    url = '/user-profile'
  } else {
    url = '/user/profile';
  }
  return url;
};

export const isValidFormatByPayerType = (input, type) => {
  let pattern;

  if (type === "individual") {
    pattern = /^[Nn]-\d+$/;
  } else if (type === "corporate") {
    pattern = /^[Cc]-\d+$/;
  } else {
    return false; // Unknown type
  }

  return pattern.test(input);
}

export const isCacValid = (cacNumber) => {
  if (typeof cacNumber !== 'string') return false;

  const pattern = /^(?:\d{1,12}|[a-zA-Z]{1,4}\d{1,12})$/;
  return pattern.test(cacNumber);
};


export const imageToBase64 = (event) => {
  return new Promise((resolve, reject) => {
    const file = event.target.files[0];
    if (!file) {
      reject(new Error("No file selected"));
      return;
    }

    const reader = new FileReader();

    reader.onload = () => resolve(reader.result.split(',')[1]); // Base64 string 
    reader.onerror = (error) => reject(error);

    reader.readAsDataURL(file); // Convert file to Base64
  });
};

export const urlToBase64 = async (url) => {
  const res = await fetch(url);
  const blob = await res.blob();

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
}

export const sortColumn = (value, sortBy, order) => {
  const floatRegex = /^\d+(\.\d+)?$/;

  return value.sort((a, b) => {
    let valA = a[sortBy];
    let valB = b[sortBy];

    // Graceful null/undefined handling
    if (valA == null) valA = "";
    if (valB == null) valB = "";

    let result = 0;

    // 1️⃣ Number / Float comparison
    if (floatRegex.test(valA) && floatRegex.test(valB)) {
      result = parseFloat(valA) - parseFloat(valB);
    } 
    // 2️⃣ Date comparison (YYYY-MM-DD HH:mm:ss)
    else if (
      /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(valA) &&
      /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(valB)
    ) {
      // Convert to timestamp for numeric comparison
      const timeA = new Date(valA.replace(" ", "T")).getTime();
      const timeB = new Date(valB.replace(" ", "T")).getTime();
      result = timeA - timeB;
    } 
    // 3️⃣ String comparison (fallback for names, text)
    else {
      result = String(valA).localeCompare(String(valB));
    }

    // 4️⃣ Apply ascending / descending order
    return order === "desc" ? -result : result;
  });
}

const extractFileNameFromUrl = (url) => {
  try {
    const pathname = new URL(url).pathname;      // /staging/doc/hello123.plain
    const fullName = pathname.split("/").pop(); // hello123.plain

    return fullName; // returns both name + extension
  } catch (e) {
    return "downloaded_file"; // fallback
  }
};

/**
 * Helper to force download a file from cloud url[ex: plain, pdf, png etc that are not configured in cloude storage for direct download]
 */
export const forceDownload = (thisScope, cloudUrl, fileName) => {
  // If filename is NOT passed, extract it from URL
  const finalFileName = fileName || extractFileNameFromUrl(cloudUrl);

  return thisScope.$http
    .post(
      "force_download",
      {
        fileUrl: cloudUrl,
        fileName: finalFileName
      },
      { responseType: "blob" }
    )
    .then((response) => {
      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/octet-stream"
      });

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = finalFileName;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    })
    .catch(err => {
      notification(thisScope, err.message, "clear", "danger");
    });
};

export const getImageBlobUrl = (thisScope, cloudUrl) => {
  return thisScope.$http
    .post(
      "force_download",
      {
        fileUrl: cloudUrl,
        fileName: "temp"
      },
      { responseType: "blob" }
    )
    .then((response) => {
      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "image/jpeg"
      });

      return URL.createObjectURL(blob);
    })
    .catch((err) => {
      notification(thisScope, err.message, "clear", "danger");
      return null;
    });
};

export const maskString = (value = "", type) => {
  if (!value || typeof value !== "string") return "";

  const trimmed = value.trim();

  if (!trimmed) return "";

  if (type === "phone") {
    const digits = trimmed.replace(/\D/g, "");

    if (digits.length <= 4) {
      return `${digits.slice(0, 1)}***${digits.slice(-1)}`;
    }
    const start = digits.slice(0, 3);
    const end = digits.slice(-3);

    return `${start}****${end}`;
  }

  if (type === "address") {
    const parts = trimmed.split(" ");

    if (parts.length <= 3) {
      return `${parts[0]}*************${parts.slice(-1)[0]}`;
    }

    const start = parts.slice(0, 2).join(" ");
    const end = parts.slice(-1)[0];

    return `${start}*************${end}`;
  }

  if (validateEmail(trimmed)) {
    const [name, domain] = trimmed.split("@");

    return `${name.slice(0, 2)}****@${domain}`;
  }

  if (trimmed.length <= 4) {
    return `${trimmed.slice(0, 1)}***`;
  }

  return `${trimmed.slice(0, 2)}***${trimmed.slice(-2)}`;
};

// Notification centralised
export const notification = (thisScope, message = 'Something went wrong', icon, color) => {
  thisScope.$notify({
    message: message,
    icon: icon,
    horizontalAlign: 'right',
    verticalAlign: 'top',
    type: color
  });
};
